import React, { useState } from "react";
import PageTitle from "../../layouts/PageTitle";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { appApi, useGetCompaniesQuery } from "../../../store/appApi";
import { createProjectApi } from "../../../services/project";
import { setActiveProject } from "../../../store/projectSlice";

const AddProject = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // GET: companies list for dropdown — RTK Query (Redux)
  const { data: companiesResponse } = useGetCompaniesQuery({
    page: 1,
    limit: 100,
  });
  const companies = Array.isArray(companiesResponse?.data)
    ? companiesResponse.data
    : [];

  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    projectName: "",
    city: "",
    underCompany: "",
    totalProjectArea: "",
    reraNumber: "",
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.projectName.trim()) e.projectName = "Project Name is required.";
    if (!form.underCompany) e.underCompany = "Please select a company.";
    if (!form.city.trim()) e.city = "City is required.";
    return e;
  };

  // POST: direct Axios call (no Redux)
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please fill in all required fields correctly.",
      });
      return;
    }

    try {
      setLoading(true);
      const response = await createProjectApi({
        projectName: form.projectName.trim(),
        city: form.city.trim(),
        totalProjectArea: form.totalProjectArea.trim(),
        reraNumber: form.reraNumber.trim(),
        underCompany: form.underCompany,
      });
      const newProject = response.data?.project;
      if (newProject) {
        dispatch(
          setActiveProject({
            id: newProject._id?.toString() ?? newProject.id,
            name: newProject.projectName,
            city: newProject.city,
          })
        );
      }
      dispatch(appApi.util.invalidateTags(["Projects"]));
      Swal.fire({
        icon: "success",
        title: "Project Created!",
        text: `${form.projectName} has been added successfully.`,
        confirmButtonText: newProject ? "Open Workspace" : "Go to Projects",
      }).then(() => {
        if (newProject) {
          navigate(`/dashboard/project/${newProject._id?.toString() ?? newProject.id}`);
        } else {
          navigate("/projects");
        }
      });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "Failed to create project. Please try again.";
      Swal.fire({ icon: "error", title: "Error", text: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => navigate("/projects");

  return (
    <>
      <PageTitle activeMenu="Add Project" motherMenu="Projects" />
      <div className="row">
        <div className="col-xl-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">Project Information</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  {/* Select Company */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">
                        Select Company <span className="text-danger">*</span>
                      </label>
                      <select
                        name="underCompany"
                        className={`form-control ${errors.underCompany ? "is-invalid" : ""}`}
                        value={form.underCompany}
                        onChange={handleChange}
                      >
                        <option value="">Select a company...</option>
                        {companies.map((company) => (
                          <option
                            key={company._id || company.id}
                            value={company._id || company.id}
                          >
                            {company.name}
                          </option>
                        ))}
                      </select>
                      {errors.underCompany && (
                        <div className="invalid-feedback">
                          {errors.underCompany}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Project Name */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">
                        Project Name <span className="text-danger">*</span>
                      </label>
                      <input
                        name="projectName"
                        type="text"
                        className={`form-control ${errors.projectName ? "is-invalid" : ""}`}
                        placeholder="Enter project name"
                        value={form.projectName}
                        onChange={handleChange}
                      />
                      {errors.projectName && (
                        <div className="invalid-feedback">
                          {errors.projectName}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* City */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">
                        City <span className="text-danger">*</span>
                      </label>
                      <input
                        name="city"
                        type="text"
                        className={`form-control ${errors.city ? "is-invalid" : ""}`}
                        placeholder="Enter city"
                        value={form.city}
                        onChange={handleChange}
                      />
                      {errors.city && (
                        <div className="invalid-feedback">{errors.city}</div>
                      )}
                    </div>
                  </div>

                  {/* RERA Number */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">RERA Number</label>
                      <input
                        name="reraNumber"
                        type="text"
                        className="form-control"
                        placeholder="Enter RERA number"
                        value={form.reraNumber}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  {/* Total Project Area */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">Total Project Area</label>
                      <input
                        name="totalProjectArea"
                        type="text"
                        className="form-control"
                        placeholder="Enter total project area"
                        value={form.totalProjectArea}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="col-12 mt-3">
                    <button
                      type="submit"
                      className="btn btn-primary me-2"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" />
                          Creating...
                        </>
                      ) : (
                        "Create Project"
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger light"
                      onClick={handleCancel}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddProject;
